import { Link } from "react-router-dom";
import { CalendarDays, Map, Gamepad2, Users, ArrowRight, Flame } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { GTA6_CLUSTERS, gta6PagesInCluster, GTA6_META } from "@/seo/gta6Pages";

const FEATURED = [
  { to: "/gta-6/release-date", label: "Release Date", icon: CalendarDays, blurb: "November 19, 2026 — confirmed launch details and platform status." },
  { to: "/gta-6/map-and-locations", label: "Map & Locations", icon: Map, blurb: "Vice City, Leonida and the regions Rockstar has officially revealed." },
  { to: "/gta-6/jason-and-lucia", label: "Jason & Lucia", icon: Users, blurb: "Meet GTA VI's central pair and separate confirmed facts from fan theories." },
  { to: "/gta-6/platforms", label: "Platforms", icon: Gamepad2, blurb: "PS5, Xbox Series X|S and the current status of PC and last-gen consoles." },
];

export default function Gta6Hub() {
  usePageMeta(GTA6_META["/gta-6"]);

  return (
    <main className="mx-auto max-w-6xl px-5 md:px-8 py-12 md:py-16" data-testid="gta6-hub">
      <section className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/10 to-transparent p-7 md:p-10 overflow-hidden relative">
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-gp-red/15 text-gp-red px-3 py-1 text-xs font-bold uppercase tracking-widest">
            <Flame size={14} /> Launch Watch
          </div>
          <h1 className="font-unbounded font-black tracking-tighter text-4xl sm:text-6xl mt-4 uppercase">GTA 6</h1>
          <p className="text-xl text-white/75 mt-4 leading-relaxed">
            GamePlatPlay's growing GTA 6 command center: confirmed Rockstar facts, launch questions, character guides, map coverage and the stories players are searching for before release.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-sm text-white/65">
            <span className="glass rounded-full px-4 py-2">Release: Nov. 19, 2026</span>
            <span className="glass rounded-full px-4 py-2">PS5</span>
            <span className="glass rounded-full px-4 py-2">Xbox Series X|S</span>
            <span className="glass rounded-full px-4 py-2">Vice City & Leonida</span>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">Start Here</p>
            <h2 className="font-unbounded font-black tracking-tight text-2xl md:text-3xl mt-1">The biggest GTA 6 questions</h2>
          </div>
          <Link to="/gta-6/everything-we-know" className="hidden sm:flex items-center gap-2 text-gp-blue text-sm font-semibold">Everything we know <ArrowRight size={16} /></Link>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURED.map(({ to, label, icon: Icon, blurb }) => (
            <Link key={to} to={to} className="glass rounded-2xl p-5 hover:bg-white/5 transition-colors group">
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-gp-blue/15 text-gp-blue"><Icon size={19} /></span>
              <h3 className="font-unbounded font-bold mt-4">{label}</h3>
              <p className="text-sm text-white/60 mt-2 leading-relaxed">{blurb}</p>
              <span className="inline-flex items-center gap-1 mt-4 text-xs font-semibold text-gp-gold group-hover:text-white">Read guide <ArrowRight size={13} /></span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <p className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">SEO Content Network</p>
        <h2 className="font-unbounded font-black tracking-tight text-2xl md:text-3xl mt-1">GamePlatPlay's GTA 6 library</h2>
        <p className="text-white/60 mt-3 max-w-3xl">We are building this the same way a major gaming publication builds authority: one strong hub connected to focused answers players are already searching for. More guides will be added as Rockstar confirms new information.</p>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {GTA6_CLUSTERS.map((cluster) => {
            const pages = gta6PagesInCluster(cluster.id);
            if (!pages.length) return null;
            return (
              <div key={cluster.id} className="glass rounded-2xl p-5">
                <h3 className="text-xs uppercase tracking-widest text-gp-gold">{cluster.name}</h3>
                <div className="mt-3 divide-y divide-white/10">
                  {pages.map((page) => (
                    <Link key={page.path} to={page.path} className="flex items-center justify-between gap-4 py-3 text-sm text-white/80 hover:text-gp-blue">
                      <span>{page.h1}</span><ArrowRight size={14} className="shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mt-12 rounded-3xl border border-gp-gold/20 bg-gp-gold/5 p-7 md:p-9">
        <p className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">GamePlatPlay Rule</p>
        <h2 className="font-unbounded font-black text-2xl mt-1">Confirmed facts first. Hype second.</h2>
        <p className="text-white/70 mt-3 max-w-3xl leading-relaxed">GTA 6 rumors will be everywhere. Our core guides will clearly separate what Rockstar has confirmed from leaks, speculation and fan theories so readers know what they can trust.</p>
      </section>
    </main>
  );
}
